import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../config/firebaseConfig";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";

const defaultCarouselSettings = {
  autoPlay: true,
  infiniteLoop: true,
  transitionTime: 900,
  interval: 5000,
  showIndicators: true,
  stopOnHover: false,
  swipeable: true,
  emulateTouch: true,
};

const createEmptySlide = () => ({ type: "image", url: "", previewUrl: "" });

export default function ManageHomeCarousel() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [carouselSettings, setCarouselSettings] = useState(
    defaultCarouselSettings,
  );
  const [carouselSlides, setCarouselSlides] = useState([createEmptySlide()]);
  const [dataLoading, setDataLoading] = useState(true);

  const [carouselSaveState, setCarouselSaveState] = useState({
    saving: false,
    success: "",
    error: "",
  });
  const [slidesSaveState, setSlidesSaveState] = useState({
    saving: false,
    success: "",
    error: "",
  });
  const [uploadingSlideIndex, setUploadingSlideIndex] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        setIsAdmin(
          userSnap.exists() && !!userSnap.data()?.roles?.includes("admin"),
        );
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const settingsRef = doc(db, "siteSettings", "homeCarousel");

    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const settingsData = snapshot.data();

          setCarouselSettings({
            autoPlay:
              typeof settingsData.autoPlay === "boolean"
                ? settingsData.autoPlay
                : defaultCarouselSettings.autoPlay,
            infiniteLoop:
              typeof settingsData.infiniteLoop === "boolean"
                ? settingsData.infiniteLoop
                : defaultCarouselSettings.infiniteLoop,
            transitionTime:
              typeof settingsData.transitionTime === "number"
                ? settingsData.transitionTime
                : defaultCarouselSettings.transitionTime,
            interval:
              typeof settingsData.interval === "number"
                ? settingsData.interval
                : defaultCarouselSettings.interval,
            showIndicators:
              typeof settingsData.showIndicators === "boolean"
                ? settingsData.showIndicators
                : defaultCarouselSettings.showIndicators,
            stopOnHover:
              typeof settingsData.stopOnHover === "boolean"
                ? settingsData.stopOnHover
                : defaultCarouselSettings.stopOnHover,
            swipeable:
              typeof settingsData.swipeable === "boolean"
                ? settingsData.swipeable
                : defaultCarouselSettings.swipeable,
            emulateTouch:
              typeof settingsData.emulateTouch === "boolean"
                ? settingsData.emulateTouch
                : defaultCarouselSettings.emulateTouch,
          });

          if (
            Array.isArray(settingsData.slides) &&
            settingsData.slides.length
          ) {
            const validSlides = settingsData.slides.filter(
              (slide) =>
                slide &&
                (slide.type === "image" || slide.type === "video") &&
                typeof slide.url === "string",
            );
            setCarouselSlides(
              validSlides.length ? validSlides : [createEmptySlide()],
            );
          } else {
            setCarouselSlides([createEmptySlide()]);
          }
        } else {
          setCarouselSettings(defaultCarouselSettings);
          setCarouselSlides([createEmptySlide()]);
        }

        setDataLoading(false);
      },
      () => {
        setDataLoading(false);
        setCarouselSaveState((prev) => ({
          ...prev,
          error: "Unable to load carousel settings.",
        }));
      },
    );

    return () => unsubscribe();
  }, []);

  const handleCarouselInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setCarouselSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : Number(value),
    }));
  };

  const saveCarouselSettings = async () => {
    setCarouselSaveState({ saving: true, success: "", error: "" });
    try {
      await setDoc(
        doc(db, "siteSettings", "homeCarousel"),
        {
          ...carouselSettings,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        },
        { merge: true },
      );
      setCarouselSaveState({
        saving: false,
        success: "Carousel settings updated successfully.",
        error: "",
      });
    } catch {
      setCarouselSaveState({
        saving: false,
        success: "",
        error: "Failed to save carousel settings. Please try again.",
      });
    }
  };

  const handleSlideFieldChange = (index, field, value) => {
    setCarouselSlides((prev) =>
      prev.map((slide, slideIndex) =>
        slideIndex === index
          ? {
              ...slide,
              [field]: value,
              previewUrl: field === "url" || field === "type" ? "" : slide.previewUrl,
            }
          : slide,
      ),
    );
  };

  const addSlideRow = () => {
    setCarouselSlides((prev) => [...prev, createEmptySlide()]);
  };

  const removeSlideRow = (index) => {
    setCarouselSlides((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, slideIndex) => slideIndex !== index);
    });
  };

  const handleSlideFileSelect = async (index, file) => {
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      setSlidesSaveState({
        saving: false,
        success: "",
        error: "Select a valid image or video file.",
      });
      return;
    }

    let localPreviewUrl = "";
    try {
      localPreviewUrl = URL.createObjectURL(file);
    } catch {
      localPreviewUrl = "";
    }

    setCarouselSlides((prev) =>
      prev.map((slide, slideIndex) =>
        slideIndex === index
          ? {
              ...slide,
              type: isVideo ? "video" : "image",
              previewUrl: localPreviewUrl,
            }
          : slide,
      ),
    );

    setUploadingSlideIndex(index);
    setSlidesSaveState((prev) => ({ ...prev, error: "", success: "" }));

    try {
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `home-carousel/${Date.now()}-${safeFileName}`;
      const mediaRef = storageRef(storage, filePath);

      await uploadBytes(mediaRef, file);
      const fileUrl = await getDownloadURL(mediaRef);

      setCarouselSlides((prev) =>
        prev.map((slide, slideIndex) =>
          slideIndex === index
            ? {
                ...slide,
                type: isVideo ? "video" : "image",
                url: fileUrl,
                previewUrl: "",
              }
            : slide,
        ),
      );

      if (localPreviewUrl) {
        try {
          URL.revokeObjectURL(localPreviewUrl);
        } catch {
        }
      }

      setSlidesSaveState({
        saving: false,
        success: "File uploaded. Click Save Carousel Media to publish.",
        error: "",
      });
    } catch {
      setSlidesSaveState({
        saving: false,
        success: "",
        error: "Upload failed. Please try again.",
      });
    } finally {
      setUploadingSlideIndex(null);
    }
  };

  const saveCarouselSlides = async () => {
    const sanitizedSlides = carouselSlides
      .map((slide) => ({
        type: slide.type === "video" ? "video" : "image",
        url: (slide.url || "").trim(),
      }))
      .filter((slide) => slide.url);

    if (sanitizedSlides.length === 0) {
      setSlidesSaveState({
        saving: false,
        success: "",
        error: "Add at least one image/video URL before saving.",
      });
      return;
    }

    setSlidesSaveState({ saving: true, success: "", error: "" });
    try {
      await setDoc(
        doc(db, "siteSettings", "homeCarousel"),
        {
          slides: sanitizedSlides,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        },
        { merge: true },
      );

      setSlidesSaveState({
        saving: false,
        success: "Carousel media saved successfully.",
        error: "",
      });
    } catch {
      setSlidesSaveState({
        saving: false,
        success: "",
        error: "Failed to save carousel media. Please try again.",
      });
    }
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <p className="text-lg text-gray-600 font-medium mb-4">
          You are not authorized to view this page.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition"
        >
          Login as Admin
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">
            Manage Home Carousel
          </h1>
          <button
            type="button"
            onClick={() => navigate("/admin-dashboard")}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Back to Admin Panel
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Carousel Settings
            </h2>
            <button
              type="button"
              onClick={() => setSettingsExpanded((prev) => !prev)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
              aria-label={
                settingsExpanded ? "Collapse carousel settings" : "Expand carousel settings"
              }
            >
              <ExpandMoreRoundedIcon
                className={`transition-transform ${settingsExpanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {!settingsExpanded && (
            <p className="text-sm text-gray-600">
              Settings minimized. Click the arrow to expand.
            </p>
          )}

          {settingsExpanded && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="autoPlay"
                    checked={carouselSettings.autoPlay}
                    onChange={handleCarouselInputChange}
                  />
                  Enable autoplay
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="infiniteLoop"
                    checked={carouselSettings.infiniteLoop}
                    onChange={handleCarouselInputChange}
                  />
                  Enable infinite loop
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="showIndicators"
                    checked={carouselSettings.showIndicators}
                    onChange={handleCarouselInputChange}
                  />
                  Show slide indicators
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="stopOnHover"
                    checked={carouselSettings.stopOnHover}
                    onChange={handleCarouselInputChange}
                  />
                  Pause on hover
                </label>
              </div>

              <div className="mt-5">
                <label className="block text-sm text-gray-700 mb-2">
                  Transition Time: {carouselSettings.transitionTime} ms
                </label>
                <input
                  type="range"
                  name="transitionTime"
                  min="300"
                  max="2500"
                  step="100"
                  value={carouselSettings.transitionTime}
                  onChange={handleCarouselInputChange}
                  className="w-full"
                />
              </div>

              <div className="mt-5">
                <label className="block text-sm text-gray-700 mb-2">
                  Slide Interval: {carouselSettings.interval} ms
                </label>
                <input
                  type="range"
                  name="interval"
                  min="2000"
                  max="12000"
                  step="500"
                  value={carouselSettings.interval}
                  onChange={handleCarouselInputChange}
                  className="w-full"
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <Button
                  onClick={saveCarouselSettings}
                  disabled={carouselSaveState.saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg shadow-sm"
                >
                  {carouselSaveState.saving
                    ? "Saving..."
                    : "Save Carousel Settings"}
                </Button>

                {carouselSaveState.success && (
                  <p className="text-green-600 text-sm">
                    {carouselSaveState.success}
                  </p>
                )}
                {carouselSaveState.error && (
                  <p className="text-red-600 text-sm">
                    {carouselSaveState.error}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Carousel Media
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Select image/video from computer or paste URL, then save to DB.
          </p>

          <div className="space-y-3">
            {carouselSlides.map((slide, index) => (
              <div
                key={`carousel-slide-${index}`}
                className="border border-gray-200 rounded-lg p-3"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">
                      Type
                    </label>
                    <select
                      value={slide.type}
                      onChange={(e) =>
                        handleSlideFieldChange(index, "type", e.target.value)
                      }
                      className="w-full border rounded-md px-2 py-2 text-sm"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">
                      Preview
                    </label>
                    <div className="w-full h-20 border rounded-md bg-gray-50 overflow-hidden flex items-center justify-center">
                      {(slide.previewUrl || slide.url) ? (
                        slide.type === "video" ? (
                          <video
                            src={slide.previewUrl || slide.url}
                            preload="metadata"
                            controls
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={slide.previewUrl || slide.url}
                            alt="Carousel slide preview"
                            className="w-full h-full object-cover"
                          />
                        )
                      ) : (
                        <span className="text-[10px] text-gray-400">
                          No media
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      URL
                    </label>
                    <input
                      type="text"
                      value={slide.url}
                      onChange={(e) =>
                        handleSlideFieldChange(index, "url", e.target.value)
                      }
                      placeholder="https://..."
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-xs text-gray-600 mb-1">
                      Select from computer
                    </label>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) =>
                        handleSlideFileSelect(index, e.target.files?.[0])
                      }
                      className="w-full text-xs"
                      disabled={uploadingSlideIndex === index}
                    />
                    {uploadingSlideIndex === index && (
                      <p className="text-xs text-blue-600 mt-1">
                        Uploading file...
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-1 flex items-end">
                    <button
                      type="button"
                      onClick={() => removeSlideRow(index)}
                      disabled={carouselSlides.length === 1}
                      className="w-full px-2 py-2 text-xs bg-red-50 text-red-700 rounded-md disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              onClick={addSlideRow}
              className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-md"
            >
              Add Slide
            </button>

            <Button
              onClick={saveCarouselSlides}
              disabled={slidesSaveState.saving || uploadingSlideIndex !== null}
              className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg shadow-sm"
            >
              {slidesSaveState.saving ? "Saving..." : "Save Carousel Media"}
            </Button>

            {slidesSaveState.success && (
              <p className="text-green-600 text-sm">
                {slidesSaveState.success}
              </p>
            )}
            {slidesSaveState.error && (
              <p className="text-red-600 text-sm">{slidesSaveState.error}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
